import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import TimeInput from '../../../components/common/TimeInput';
import { useAuth } from '../../../contexts/AuthContext';
import { byggProposalText, harRedigerbarMall } from '../utils/proposalEmailTemplates';
import {
  MEETING_TEMPLATES,
  OFFICE_ADDRESS,
  LOCATION_TYPE_LABELS,
  getAttendeeRoleLabel,
  getMeetingDisplayName,
  formatScheduledAt,
  formatDuration
} from '../utils/meetingTemplates';

// Måste matcha taket i upload.array('homeworkFile', 5) i meetingRoutes.js —
// multer kastar LIMIT_UNEXPECTED_FILE på den sjätte filen, och då går hela
// utskicket av tidsförslagen i backen, inte bara bilagan.
const MAX_HEMLAXEFILER = 5;

const MeetingModal = ({
  isOpen,
  onClose,
  meeting = null, // null = skapa nytt, objekt = redigera
  project,
  availableTypes = [],
  briefItems = [], // Brief-items för agenda-populering (avstämningsmöte)
  onSave,
  onSaveAndPropose, // Ny: skapa möte och skicka tidsförslag i ett steg
  onSaveAndBook, // Ny: skapa möte och boka direkt
  onPropose,
  onBook,
  onCancel,
  onComplete,
  onDelete
}) => {
  const { user } = useAuth();
  const isEditing = Boolean(meeting);

  // Form state
  const [formData, setFormData] = useState({
    type: '',
    locationType: 'customer',
    customAddress: '',
    attendees: [],
    agenda: [],
    topic: '',
    duration: 60 // minuter — sätts från mötestypens default vid typ-val
  });

  // Steg-baserat flöde
  const [currentStep, setCurrentStep] = useState(1); // 1 = Detaljer, 2 = Agenda, 3 = Bokning

  // Bokningsmetod
  const [bookingMethod, setBookingMethod] = useState('propose'); // 'propose' eller 'direct'

  // Tidsförslag state (för föreslå tider)
  const [proposals, setProposals] = useState([
    { date: '', time: '' },
    { date: '', time: '' },
    { date: '', time: '' }
  ]);
  const [proposalMessage, setProposalMessage] = useState('');

  // Har användaren rört mailtexten? Då slutar vi skriva över den när typ, längd
  // eller bifogad fil ändras — annars raderas hennes formuleringar tyst.
  const [mailtextRedigerad, setMailtextRedigerad] = useState(false);

  // Hemläxa-fil state
  // Flera hemläxebilagor: IA skickar ofta ett underlag per område, t.ex. ett
  // för färgsättning och ett för badrummet.
  const [homeworkFiles, setHomeworkFiles] = useState([]);

  // Direktbokning state
  const [directBooking, setDirectBooking] = useState({
    date: '',
    time: ''
  });

  // Återkommande möten state (endast för byggmöte)
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringConfig, setRecurringConfig] = useState({
    endType: 'count', // 'count' eller 'date'
    count: 4,
    endDate: ''
  });

  // Deltagare-val (för alla mötestyper)
  const [selectedAttendees, setSelectedAttendees] = useState({});

  // UI state
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Bygg lista över alla möjliga deltagare från projektet
  const buildAllPossibleAttendees = () => {
    const attendees = [];

    // Kunder
    if (project?.customer) {
      attendees.push({
        id: project.customer._id,
        name: `${project.customer.firstName || ''} ${project.customer.lastName || ''}`.trim(),
        email: project.customer.email,
        role: 'customer',
        roleLabel: 'Kund'
      });
    }
    if (project?.additionalCustomers?.length > 0) {
      project.additionalCustomers.forEach((c, idx) => {
        if (c && c.email) {
          attendees.push({
            id: c._id,
            name: `${c.firstName || ''} ${c.lastName || ''}`.trim(),
            email: c.email,
            role: 'customer',
            roleLabel: `Kund ${idx + 2}`
          });
        }
      });
    }

    // Byggkonsult
    if (project?.consultant) {
      attendees.push({
        id: project.consultant._id,
        name: `${project.consultant.firstName || ''} ${project.consultant.lastName || ''}`.trim(),
        email: project.consultant.email,
        role: 'consultant',
        roleLabel: 'Byggkonsult'
      });
    }

    // Inredningsarkitekt
    if (project?.architect) {
      attendees.push({
        id: project.architect._id,
        name: `${project.architect.firstName || ''} ${project.architect.lastName || ''}`.trim(),
        email: project.architect.email,
        role: 'architect',
        roleLabel: 'Inredningsarkitekt'
      });
    }

    // Byggare (huvudkontakt)
    if (project?.builderContactPerson) {
      attendees.push({
        id: project.builderContactPerson._id,
        name: `${project.builderContactPerson.firstName || ''} ${project.builderContactPerson.lastName || ''}`.trim(),
        email: project.builderContactPerson.email,
        role: 'builder',
        roleLabel: 'Byggare',
        companyName: project.builder?.companyName
      });
    }

    // Byggare (ytterligare kontakter)
    if (project?.builder?.additionalContactPersons?.length > 0) {
      project.builder.additionalContactPersons.forEach(c => {
        if (c && c.email) {
          attendees.push({
            id: c._id,
            name: `${c.firstName || ''} ${c.lastName || ''}`.trim(),
            email: c.email,
            role: 'builder',
            roleLabel: 'Byggare',
            companyName: project.builder?.companyName
          });
        }
      });
    }

    // Underentreprenörer
    if (project?.subcontractors?.length > 0) {
      project.subcontractors.forEach(ue => {
        if (ue && ue.email) {
          attendees.push({
            id: ue._id,
            name: `${ue.firstName || ''} ${ue.lastName || ''}`.trim(),
            email: ue.email,
            role: 'subcontractor',
            roleLabel: 'UE',
            companyName: ue.companyName
          });
        }
      });
    }

    // Projektörer
    if (project?.projectors?.length > 0) {
      project.projectors.forEach(p => {
        if (p && p.email) {
          attendees.push({
            id: p._id,
            name: `${p.firstName || ''} ${p.lastName || ''}`.trim(),
            email: p.email,
            role: 'projector',
            roleLabel: 'Projektör',
            companyName: p.companyName
          });
        }
      });
    }

    // Materialleverantörer
    if (project?.materialSuppliers?.length > 0) {
      project.materialSuppliers.forEach(ms => {
        const contactPerson = ms.contactPerson;
        if (contactPerson && contactPerson.email) {
          attendees.push({
            id: contactPerson._id,
            name: `${contactPerson.firstName || ''} ${contactPerson.lastName || ''}`.trim(),
            email: contactPerson.email,
            role: 'materialSupplier',
            roleLabel: 'Materiallev.',
            companyName: ms.supplier?.companyName || ''
          });
        }
      });
    }

    // Deduplicera — samma person kan finnas i flera roller
    const seen = new Set();
    return attendees.filter(a => {
      if (seen.has(a.id)) return false;
      seen.add(a.id);
      return true;
    });
  };

  const allPossibleAttendees = buildAllPossibleAttendees();

  // IA-mötestyper har alltid bara kund + arkitekt — dölj deltagarlistan
  const isIAMeetingType = ['intromote', 'hemlaxa', 'avstamning', 'fargkonsultation'].includes(formData.type);

  // Initiera valda deltagare baserat på mötestyp
  const initializeSelectedAttendees = (meetingType) => {
    const template = MEETING_TEMPLATES[meetingType];
    const defaultRoles = template?.defaultAttendeeRoles || [];

    const selected = {};
    allPossibleAttendees.forEach(attendee => {
      if (defaultRoles.includes(attendee.role)) {
        if (attendee.role === 'builder') {
          const isFirstBuilder = allPossibleAttendees.filter(a => a.role === 'builder')[0]?.id === attendee.id;
          selected[attendee.id] = isFirstBuilder;
        } else {
          selected[attendee.id] = true;
        }
      } else {
        selected[attendee.id] = false;
      }
    });

    return selected;
  };

  /**
   * Uppdragspunkter i agendan identifieras på briefId (kategori + nyckel), inte
   * på titeln. "Bad" finns i både Ritningar 2D och Design 3D — med titeln som
   * nyckel kryssades båda i och ur samtidigt.
   *
   * Titeln får kategorin framför sig när namnet finns i flera kategorier, så
   * kunden ser "Design 3D – Bad" i agendan och inte bara "Bad".
   */
  const agendaTitel = (item) => (
    briefItems.filter(b => b.label === item.label).length > 1
      ? `${item.category} – ${item.label}`
      : item.label
  );
  // Möten sparade före briefId saknar det — då får titeln avgöra, som förut.
  const arVald = (a, item) => !a.isCustom && (a.briefId
    ? a.briefId === item.id
    : a.title === item.label || a.title === agendaTitel(item));

  // Beräkna initial agenda baserat på typ och briefItems
  const getInitialAgenda = (type) => {
    const template = MEETING_TEMPLATES[type];
    if ((type === 'avstamning' || type === 'hemlaxa') && briefItems.length > 0) {
      return briefItems.map((item, idx) => ({
        title: agendaTitel(item),
        order: idx + 1,
        isCustom: false,
        briefId: item.id
      }));
    }
    return template?.agenda || [];
  };

  // Kolla om typen ska ha Teams som förvald plats
  const getInitialLocation = (type) => {
    if (type === 'intromote' || type === 'avstamning' || type === 'hemlaxa') {
      return { locationType: 'teams', customAddress: '' };
    }
    if (type === 'fargkonsultation') {
      return { locationType: 'office', customAddress: '' };
    }
    return { locationType: 'customer', customAddress: '' };
  };

  // Initiera form med befintlig data eller defaults
  /**
   * Förifyll mailtexten för de mötestyper som har en mall.
   *
   * Följer typ, längd, agenda och bifogad fil — väljer man 60 min i stället för
   * 45 ska texten säga 60. Skriver man om den själv slutar vi röra den, så att
   * ett byte av längd inte raderar formuleringarna. "Återställ till
   * standardtext" nollar flaggan och tar tillbaka mallen.
   */
  useEffect(() => {
    if (!harRedigerbarMall(formData.type) || mailtextRedigerad) return;
    setProposalMessage(byggProposalText({
      type: formData.type,
      iaFornamn: user?.firstName || 'Jag',
      minuter: formData.duration,
      antalFiler: homeworkFiles.length,
      agenda: formData.agenda
    }));
  }, [formData.type, formData.duration, formData.agenda, homeworkFiles, mailtextRedigerad, user]);

  useEffect(() => {
    if (meeting) {
      setFormData({
        type: meeting.type,
        // Teams sparas som en anpassad plats med "Microsoft Teams" — visa den
        // som Teams-valet, inte som "Annan plats" med texten ifylld.
        ...(() => {
          const typ = meeting.location?.type || 'customer';
          const plats = `${meeting.location?.address || ''} ${meeting.location?.displayName || ''}`;
          if (typ === 'custom' && /teams/i.test(plats)) {
            return { locationType: 'teams', customAddress: '' };
          }
          return { locationType: typ, customAddress: typ === 'custom' ? meeting.location.address : '' };
        })(),
        attendees: meeting.attendees || [],
        agenda: meeting.agenda || [],
        topic: meeting.topic || ''
      });
      const selected = {};
      meeting.attendees?.forEach(a => {
        const attendeeId = a.userId?._id || a.userId;
        if (attendeeId) selected[attendeeId] = true;
      });
      setSelectedAttendees(selected);
      // Vid redigering, starta på steg 1
      setCurrentStep(1);
    } else if (availableTypes.length > 0) {
      const firstType = availableTypes[0];
      const location = getInitialLocation(firstType.type);
      setFormData({
        type: firstType.type,
        locationType: location.locationType,
        customAddress: location.customAddress,
        attendees: [],
        agenda: getInitialAgenda(firstType.type),
        topic: '',
        duration: MEETING_TEMPLATES[firstType.type]?.duration || 60
      });
      setSelectedAttendees(initializeSelectedAttendees(firstType.type));
      setCurrentStep(1);
    }
    // getInitialAgenda och initializeSelectedAttendees är UTELÄMNADE MED AVSIKT.
    //
    // Effekten INITIERAR formuläret och ska bara köra när det finns ett nytt
    // möte att initiera från. Hjälparna läser allPossibleAttendees, som byggs om
    // vid varje render (se buildAllPossibleAttendees ovan), och briefItems, som
    // är ett defaultargument `= []` och alltså också nytt varje render. Slås de
    // in i useCallback får de nya referenser varje render, effekten kör om, och
    // typ, plats, agenda, ämne och deltagare nollställs medan man fyller i.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting, availableTypes, project]);

  // Hantera typ-ändring
  const handleTypeChange = (newType) => {
    const location = getInitialLocation(newType);
    const template = MEETING_TEMPLATES[newType];
    setFormData(prev => ({
      ...prev,
      type: newType,
      locationType: location.locationType,
      customAddress: location.customAddress,
      agenda: getInitialAgenda(newType),
      topic: '',
      duration: template?.duration || 60
    }));
    setSelectedAttendees(initializeSelectedAttendees(newType));
  };

  // Bygg deltagarlista baserat på val
  const buildSelectedAttendeesList = () => {
    return allPossibleAttendees
      .filter(attendee => selectedAttendees[attendee.id])
      .map(attendee => ({
        userId: attendee.id,
        email: attendee.email,
        name: attendee.name,
        role: attendee.role
      }));
  };

  // Bygg mötesdata
  const buildMeetingData = () => ({
    type: formData.type,
    locationType: formData.locationType,
    customAddress: formData.locationType === 'custom' ? formData.customAddress : undefined,
    attendees: buildSelectedAttendeesList(),
    agenda: formData.agenda,
    topic: formData.topic || undefined,
    duration: formData.duration
  });

  // Hantera spara som utkast
  const handleSaveAsDraft = async () => {
    try {
      setSaving(true);
      await onSave(buildMeetingData());
      onClose();
    } catch (err) {
      console.error('Fel vid sparande:', err);
      alert(err.response?.data?.message || 'Kunde inte spara mötet');
    } finally {
      setSaving(false);
    }
  };

  // Hantera agendapunkt-ändring
  const handleAgendaChange = (index, newTitle) => {
    setFormData(prev => ({
      ...prev,
      agenda: prev.agenda.map((item, i) =>
        i === index ? { ...item, title: newTitle } : item
      )
    }));
  };

  // Ta bort agendapunkt
  const removeAgendaItem = (index) => {
    setFormData(prev => ({
      ...prev,
      agenda: prev.agenda.filter((_, i) => i !== index)
    }));
  };

  // Kolla om det finns giltiga tidsförslag
  const hasValidProposals = () => {
    return proposals.some(p => p.date && p.time);
  };

  // Kolla om direktbokning är giltig
  const hasValidDirectBooking = () => {
    if (!directBooking.date || !directBooking.time) return false;

    // Om återkommande är aktiverat, validera även det
    if (isRecurring && formData.type === 'byggmote') {
      if (recurringConfig.endType === 'count') {
        return recurringConfig.count >= 2;
      } else {
        return recurringConfig.endDate && recurringConfig.endDate > directBooking.date;
      }
    }

    return true;
  };

  // Beräkna antal återkommande möten
  const getRecurringCount = () => {
    if (!isRecurring || formData.type !== 'byggmote' || !directBooking.date) return 1;

    if (recurringConfig.endType === 'count') {
      return recurringConfig.count;
    } else if (recurringConfig.endDate) {
      const startDate = new Date(`${directBooking.date}T12:00:00`);
      const endDate = new Date(`${recurringConfig.endDate}T12:00:00`);
      const weeks = Math.floor((endDate - startDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
      return Math.max(1, weeks);
    }
    return 1;
  };

  // Hantera skicka tidsförslag
  const handleSendProposals = async () => {
    const validProposals = proposals.filter(p => p.date && p.time);
    if (validProposals.length === 0) {
      alert('Ange minst ett tidsförslag');
      return;
    }

    try {
      setSaving(true);

      const hwFile = formData.type === 'hemlaxa' ? homeworkFiles : [];

      if (isEditing) {
        await onPropose(meeting._id, validProposals, proposalMessage, hwFile);
      } else {
        const meetingData = buildMeetingData();
        if (onSaveAndPropose) {
          await onSaveAndPropose(meetingData, validProposals, proposalMessage, hwFile);
        } else {
          const newMeeting = await onSave(meetingData);
          if (newMeeting?._id) {
            await onPropose(newMeeting._id, validProposals, proposalMessage, hwFile);
          }
        }
      }

      onClose();
    } catch (err) {
      console.error('Fel vid skickande av tidsförslag:', err);
      alert(err.response?.data?.message || 'Kunde inte skicka tidsförslag');
    } finally {
      setSaving(false);
    }
  };

  // Hantera direktbokning
  const handleDirectBook = async () => {
    if (!directBooking.date || !directBooking.time) {
      alert('Välj datum och tid');
      return;
    }

    try {
      setSaving(true);

      // Bygg bokningsdata
      // Längden måste med — bokar man ett BEFINTLIGT möte sparas inte formuläret
      // först, så en ändrad längd hade annars tappats och mötestypens standardtid
      // använts i Outlook-inbjudan.
      const bookingData = {
        date: directBooking.date,
        time: directBooking.time,
        duration: formData.duration
      };

      // Lägg till återkommande-data för byggmöte
      if (isRecurring && formData.type === 'byggmote') {
        bookingData.recurring = {
          enabled: true,
          endType: recurringConfig.endType,
          count: recurringConfig.endType === 'count' ? recurringConfig.count : undefined,
          endDate: recurringConfig.endType === 'date' ? recurringConfig.endDate : undefined
        };
      }

      if (isEditing) {
        // Befintligt möte - boka direkt
        await onBook(meeting._id, bookingData);
      } else {
        // Nytt möte - skapa och boka i ett steg
        const meetingData = buildMeetingData();
        if (onSaveAndBook) {
          await onSaveAndBook(meetingData, bookingData);
        } else {
          // Fallback: skapa möte först, sedan boka
          const newMeeting = await onSave(meetingData);
          if (newMeeting?._id) {
            await onBook(newMeeting._id, bookingData);
          }
        }
      }

      onClose();
    } catch (err) {
      console.error('Fel vid direktbokning:', err);
      alert(err.response?.data?.message || 'Kunde inte boka mötet');
    } finally {
      setSaving(false);
    }
  };

  // Nästa steg
  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Föregående steg
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const selectedTemplate = MEETING_TEMPLATES[formData.type];

  // Steg-indikator
  const steps = [
    { num: 1, label: 'Detaljer' },
    { num: 2, label: 'Agenda' },
    { num: 3, label: 'Bokning' }
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="default">
          {/* Header */}
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b -mx-4 sm:-mx-8 -mt-4 sm:-mt-8 mb-4 rounded-t-xl sm:rounded-t-lg">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? getMeetingDisplayName(meeting) : 'Nytt möte'}
            </h2>

            {/* Status för befintligt möte */}
            {isEditing && (
              <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
                {meeting.scheduledAt && (
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {formatScheduledAt(meeting.scheduledAt)}
                  </span>
                )}
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {formatDuration(meeting.duration)}
                </span>
              </div>
            )}
          </div>

          {/* Steg-indikator */}
          {(!isEditing || meeting.status === 'draft') && (
            <div className="px-4 sm:px-6 py-3 border-b bg-white -mx-4 sm:-mx-8">
              <div className="flex items-center justify-between">
                {steps.map((step, idx) => (
                  <React.Fragment key={step.num}>
                    <button
                      onClick={() => setCurrentStep(step.num)}
                      className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                        currentStep === step.num
                          ? 'bg-leadme-pink text-white'
                          : currentStep > step.num
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      <span className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                        currentStep === step.num
                          ? 'bg-white/20'
                          : currentStep > step.num
                            ? 'bg-green-200'
                            : 'bg-gray-200'
                      }`}>
                        {currentStep > step.num ? (
                          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : step.num}
                      </span>
                      {step.label}
                    </button>
                    {idx < steps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-1 sm:mx-2 ${currentStep > step.num ? 'bg-green-300' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="px-4 sm:px-6 py-4 max-h-[50vh] sm:max-h-[60vh] overflow-y-auto">
            {/* Steg 1: Detaljer */}
            {currentStep === 1 && (
              <div className="space-y-4">
                {/* Mötestyp */}
                {!isEditing && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Typ av möte
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => handleTypeChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary"
                    >
                      {availableTypes.map(type => (
                        <option key={type.type} value={type.type}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {selectedTemplate?.description && (
                      <p className="mt-1 text-sm text-gray-500">{selectedTemplate.description}</p>
                    )}
                  </div>
                )}

                {/* Tidsspann — kan justeras från mötestypens default */}
                {!isEditing && formData.type && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tidsspann
                    </label>
                    <select
                      value={formData.duration}
                      onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value, 10) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary"
                    >
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>1 timme</option>
                      <option value={90}>1,5 timme</option>
                      <option value={120}>2 timmar</option>
                    </select>
                  </div>
                )}

                {/* Ämne (för byggmöte, avstämning och hemläxa) */}
                {(formData.type === 'byggmote' || formData.type === 'avstamning' || formData.type === 'hemlaxa') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {formData.type === 'byggmote' ? 'Mötesnamn (valfritt)' : 'Ämne (valfritt)'}
                    </label>
                    <input
                      type="text"
                      value={formData.topic}
                      onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                      placeholder={formData.type === 'byggmote' ? 'T.ex. plattsättning, elsättning...' : formData.type === 'hemlaxa' ? 'T.ex. kök, badrum...' : 'T.ex. el, badrum, material...'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.type === 'byggmote' ? 'Lämna tomt för "Byggmöte #X". Visas i rubrik och kalenderinbjudan.' : 'Visas i mötesrubriken och kalenderinbjudan'}
                    </p>
                  </div>
                )}

                {/* Hemläxa-filer (bara för hemläxa-möte) */}
                {formData.type === 'hemlaxa' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bifoga hemläxa-dokument (valfritt)
                    </label>
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                      <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <span className="text-gray-700">
                        {homeworkFiles.length > 0 ? 'Lägg till fler filer...' : 'Välj filer...'}
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        multiple
                        accept=".pptx,.ppt,.pdf,.docx,.doc"
                        onChange={(e) => {
                          // Lägg till i stället för att ersätta — väljer man en
                          // mapp i taget ska det förra valet inte försvinna.
                          const nya = Array.from(e.target.files || []);
                          setHomeworkFiles(forra => {
                            const samlade = [...forra];
                            nya.forEach(fil => {
                              const finns = samlade.some(f => f.name === fil.name && f.size === fil.size);
                              if (!finns && samlade.length < MAX_HEMLAXEFILER) samlade.push(fil);
                            });
                            return samlade;
                          });
                          e.target.value = '';
                        }}
                      />
                    </label>

                    {homeworkFiles.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {homeworkFiles.map((fil, idx) => (
                          <li key={`${fil.name}-${fil.size}`} className="flex items-center justify-between gap-2 px-3 py-1.5 bg-gray-50 rounded text-sm">
                            <span className="min-w-0 break-all text-gray-700">{fil.name}</span>
                            <button
                              type="button"
                              onClick={() => setHomeworkFiles(forra => forra.filter((_, i) => i !== idx))}
                              className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                              aria-label={`Ta bort ${fil.name}`}
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    <p className="mt-1 text-xs text-gray-500">
                      Bifogas i hemläxa-mailet till kunden — ett per område går bra, t.ex. färgsättning och bad.
                      PPT, PDF eller Word, högst {MAX_HEMLAXEFILER} filer.
                    </p>
                  </div>
                )}

                {/* Plats */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plats
                  </label>
                  <div className="space-y-2">
                    {Object.entries(LOCATION_TYPE_LABELS).map(([type, label]) => (
                      <label key={type} className="flex items-center">
                        <input
                          type="radio"
                          name="locationType"
                          value={type}
                          checked={formData.locationType === type}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            locationType: e.target.value
                          }))}
                          className="mr-2 text-primary focus:ring-primary"
                        />
                        <span className="text-sm">
                          {label}
                          {type === 'customer' && project && (
                            <span className="text-gray-500 ml-1">
                              ({project.address}, {project.postCode} {project.city})
                            </span>
                          )}
                          {type === 'office' && (
                            <span className="text-gray-500 ml-1">({OFFICE_ADDRESS})</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Ett gammalt möte med egen adress (före "Annan plats" togs
                      bort) visar adressen, så att den inte försvinner tyst. */}
                  {formData.locationType === 'custom' && formData.customAddress && (
                    <p className="mt-2 text-sm text-gray-600">Plats: {formData.customAddress}</p>
                  )}
                </div>

                {/* Deltagare (döljs för IA-möten — alltid kund + arkitekt) */}
                {!isEditing && !isIAMeetingType && allPossibleAttendees.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deltagare
                    </label>
                    <div className="space-y-1 max-h-64 overflow-y-auto">
                      {allPossibleAttendees.map((attendee) => (
                        <label
                          key={attendee.id}
                          className="flex items-center p-2.5 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedAttendees[attendee.id] || false}
                            onChange={(e) => setSelectedAttendees(prev => ({
                              ...prev,
                              [attendee.id]: e.target.checked
                            }))}
                            className="mr-3 text-primary focus:ring-primary rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">
                                {attendee.name || attendee.email}
                              </span>
                              <span className="text-xs text-gray-500 flex-shrink-0">
                                ({attendee.roleLabel})
                              </span>
                            </div>
                            {attendee.companyName && (
                              <span className="text-xs text-gray-400">{attendee.companyName}</span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      {Object.values(selectedAttendees).filter(Boolean).length} av {allPossibleAttendees.length} deltagare valda
                    </p>
                  </div>
                )}

                {/* Anteckningsfältet är borttaget med flit.
                    Det sparades på Meeting.notes men lästes aldrig någonstans —
                    varken i gränssnittet eller i kalenderinbjudan, som bygger sin
                    text av agendan. En IA skrev ett meddelande till kunden här och
                    undrade sedan var det tog vägen; svaret var ingenstans.
                    Agendan i steg 2 är det som når deltagarna, och löpande
                    anteckningar tas i Möten-vyn när mötet är bokat. */}

                {/* Deltagare (visning för befintligt möte) */}
                {isEditing && meeting.attendees?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deltagare ({meeting.attendees.length})
                    </label>
                    <div className="space-y-2">
                      {meeting.attendees.map((attendee, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg text-sm"
                        >
                          <div>
                            <span className="font-medium">{attendee.name}</span>
                            <span className="text-gray-500 ml-2">
                              ({getAttendeeRoleLabel(attendee.role)})
                            </span>
                          </div>
                          <span className="text-gray-400">{attendee.email}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Steg 2: Agenda */}
            {currentStep === 2 && (
              <div className="space-y-3">
                {/* Brief-items som checkboxar (bara för avstämning/hemläxa) */}
                {briefItems.length > 0 && (formData.type === 'avstamning' || formData.type === 'hemlaxa') && (
                  <div className="space-y-2 mb-4">
                    <p className="text-sm font-medium text-gray-700">Välj från uppdraget</p>
                    {briefItems.map(item => {
                      const isSelected = formData.agenda.some(a => arVald(a, item));
                      const statusLabel = {
                        attgora: 'Att göra',
                        pagaende: 'Pågående',
                        granskning: 'Granskning',
                        klart: 'Klart'
                      }[item.status] || item.status;
                      const statusColor = {
                        attgora: 'text-gray-500 bg-gray-100',
                        pagaende: 'text-yellow-700 bg-yellow-100',
                        granskning: 'text-orange-700 bg-orange-100',
                        klart: 'text-green-700 bg-green-100'
                      }[item.status] || 'text-gray-500 bg-gray-100';
                      return (
                        <label key={item.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              if (isSelected) {
                                setFormData(prev => ({
                                  ...prev,
                                  agenda: prev.agenda.filter(a => !arVald(a, item))
                                }));
                              } else {
                                setFormData(prev => ({
                                  ...prev,
                                  agenda: [...prev.agenda, {
                                    title: agendaTitel(item),
                                    order: prev.agenda.length + 1,
                                    isCustom: false,
                                    briefId: item.id
                                  }]
                                }));
                              }
                            }}
                            className="mt-0.5 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          <div>
                            <span className="text-sm text-gray-900">{item.label}</span>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-gray-500">{item.category}</span>
                              <span className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 ${statusColor}`}>{statusLabel}</span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                    <div className="border-b border-gray-200 mt-2" />
                  </div>
                )}

                <p className="text-sm text-gray-600 mb-2">
                  {briefItems.length > 0 && (formData.type === 'avstamning' || formData.type === 'hemlaxa') ? 'Egna agendapunkter' : 'Anpassa agendan för mötet. Du kan lägga till, ta bort eller ändra ordningen på punkterna.'}
                </p>

                {formData.agenda.filter(item => item.isCustom || !(briefItems.length > 0 && (formData.type === 'avstamning' || formData.type === 'hemlaxa'))).map((item, idx) => {
                  const realIndex = formData.agenda.indexOf(item);
                  return (
                    <div key={realIndex} className="flex items-center gap-2">
                      <span className="text-gray-400 text-sm w-6">{idx + 1}.</span>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleAgendaChange(realIndex, e.target.value)}
                        placeholder="Agendapunkt..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary"
                      />
                      <button
                        onClick={() => removeAgendaItem(realIndex)}
                        className="p-2 text-gray-400 hover:text-red-500"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}

                <button
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      agenda: [
                        ...prev.agenda,
                        { title: '', order: prev.agenda.length + 1, isCustom: true }
                      ]
                    }));
                  }}
                  className="w-full py-2 border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 text-sm"
                >
                  + Lägg till agendapunkt
                </button>
              </div>
            )}

            {/* Steg 3: Bokning */}
            {currentStep === 3 && (
              <div className="space-y-4">
                {/* Bokningsmetod-väljare */}
                <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setBookingMethod('propose')}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      bookingMethod === 'propose'
                        ? 'bg-leadme-pink text-white'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Föreslå tider
                    </div>
                    <p className={`text-xs mt-1 ${bookingMethod === 'propose' ? 'text-white/80' : 'text-gray-400'}`}>
                      Låt deltagarna välja
                    </p>
                  </button>
                  <button
                    onClick={() => setBookingMethod('direct')}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      bookingMethod === 'direct'
                        ? 'bg-leadme-pink text-white'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Boka direkt
                    </div>
                    <p className={`text-xs mt-1 ${bookingMethod === 'direct' ? 'text-white/80' : 'text-gray-400'}`}>
                      Tiden är redan bestämd
                    </p>
                  </button>
                </div>

                {/* Föreslå tider */}
                {bookingMethod === 'propose' && (
                  <div className="space-y-4 pt-2">
                    <p className="text-sm text-gray-600">
                      Föreslå upp till 3 tider. Deltagarna får ett e-postmeddelande och kan markera vilka tider som passar.
                    </p>

                    {proposals.map((proposal, index) => (
                      <div key={index} className="flex gap-3">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">
                            Förslag {index + 1} - Datum
                          </label>
                          <input
                            type="date"
                            value={proposal.date}
                            onChange={(e) => {
                              const newProposals = [...proposals];
                              newProposals[index] = { ...newProposals[index], date: e.target.value };
                              setProposals(newProposals);
                            }}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary"
                          />
                        </div>
                        <div className="w-32">
                          <label className="block text-xs text-gray-500 mb-1">Tid</label>
                          <TimeInput
                            value={proposal.time}
                            onChange={(e) => {
                              const newProposals = [...proposals];
                              newProposals[index] = { ...newProposals[index], time: e.target.value };
                              setProposals(newProposals);
                            }}
                          />
                        </div>
                      </div>
                    ))}

                    {/* För IA-mötena visas hela mailet, redigerbart. Texten låg
                        tidigare hårdkodad i backend, så man kunde varken se vad
                        kunden fick eller ändra ett ord. */}
                    {harRedigerbarMall(formData.type) ? (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Mailet till kunden
                          </label>
                          {mailtextRedigerad && (
                            <button
                              type="button"
                              onClick={() => setMailtextRedigerad(false)}
                              className="text-xs text-primary hover:underline"
                            >
                              Återställ till standardtext
                            </button>
                          )}
                        </div>
                        <textarea
                          value={proposalMessage}
                          onChange={(e) => {
                            setProposalMessage(e.target.value);
                            setMailtextRedigerad(true);
                          }}
                          rows={12}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Hälsningen (&quot;Hej Anna,&quot;), knappen till kundportalen och din
                          underskrift med projekt-id läggs till automatiskt — de kan inte tas bort.
                          Tom rad ger nytt stycke.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Meddelande till deltagare (valfritt)
                        </label>
                        <textarea
                          value={proposalMessage}
                          onChange={(e) => setProposalMessage(e.target.value)}
                          placeholder="T.ex. 'Välj den tid som passar dig bäst...'"
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Boka direkt */}
                {bookingMethod === 'direct' && (
                  <div className="space-y-4 pt-2">
                    <p className="text-sm text-gray-600">
                      Ange datum och tid för mötet. En kalenderinbjudan skickas direkt till alla deltagare.
                    </p>

                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {isRecurring && formData.type === 'byggmote' ? 'Startdatum' : 'Datum'}
                        </label>
                        <input
                          type="date"
                          value={directBooking.date}
                          onChange={(e) => setDirectBooking(prev => ({ ...prev, date: e.target.value }))}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div className="w-40">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tid
                        </label>
                        <TimeInput
                          value={directBooking.time}
                          onChange={(e) => setDirectBooking(prev => ({ ...prev, time: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Återkommande möten - endast för byggmöte och efter datum+tid valts */}
                    {formData.type === 'byggmote' && directBooking.date && directBooking.time && (
                      <div className="border-t pt-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isRecurring}
                            onChange={(e) => setIsRecurring(e.target.checked)}
                            className="rounded text-primary focus:ring-primary"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Återkommande byggmöte
                          </span>
                        </label>

                        {isRecurring && (
                          <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                            <p className="text-sm text-blue-800">
                              Möten bokas varje{' '}
                              <span className="font-medium">
                                {directBooking.date
                                  ? new Date(`${directBooking.date}T12:00:00`).toLocaleDateString('sv-SE', { weekday: 'long' })
                                  : '(välj datum)'}
                              </span>
                              {' '}kl. {directBooking.time || '(välj tid)'}
                            </p>

                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="recurringEndType"
                                  value="count"
                                  checked={recurringConfig.endType === 'count'}
                                  onChange={() => setRecurringConfig(prev => ({ ...prev, endType: 'count' }))}
                                  className="text-primary focus:ring-primary"
                                />
                                <span className="text-sm text-gray-700">Antal möten</span>
                              </label>
                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="recurringEndType"
                                  value="date"
                                  checked={recurringConfig.endType === 'date'}
                                  onChange={() => setRecurringConfig(prev => ({ ...prev, endType: 'date' }))}
                                  className="text-primary focus:ring-primary"
                                />
                                <span className="text-sm text-gray-700">Till datum</span>
                              </label>
                            </div>

                            {recurringConfig.endType === 'count' ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="2"
                                  max="52"
                                  value={recurringConfig.count}
                                  onChange={(e) => setRecurringConfig(prev => ({
                                    ...prev,
                                    count: Math.min(52, Math.max(2, parseInt(e.target.value) || 2))
                                  }))}
                                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary"
                                />
                                <span className="text-sm text-gray-600">möten totalt</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Fram till:</span>
                                <input
                                  type="date"
                                  value={recurringConfig.endDate}
                                  onChange={(e) => setRecurringConfig(prev => ({ ...prev, endDate: e.target.value }))}
                                  min={directBooking.date || new Date().toISOString().split('T')[0]}
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary"
                                />
                              </div>
                            )}

                            {/* Förhandsvisning */}
                            {directBooking.date && directBooking.time && (
                              <div className="text-xs text-blue-700 mt-2">
                                {(() => {
                                  const startDate = new Date(`${directBooking.date}T12:00:00`);
                                  let count = 0;
                                  if (recurringConfig.endType === 'count') {
                                    count = recurringConfig.count;
                                  } else if (recurringConfig.endDate) {
                                    const endDate = new Date(`${recurringConfig.endDate}T12:00:00`);
                                    const weeks = Math.floor((endDate - startDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
                                    count = Math.max(1, weeks);
                                  }
                                  if (count > 0) {
                                    const lastDate = new Date(startDate);
                                    lastDate.setDate(lastDate.getDate() + (count - 1) * 7);
                                    return `${count} möten bokas (${startDate.toLocaleDateString('sv-SE')} - ${lastDate.toLocaleDateString('sv-SE')})`;
                                  }
                                  return '';
                                })()}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {directBooking.date && directBooking.time && !isRecurring && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">
                          <span className="font-medium">Mötet bokas:</span>{' '}
                          {new Date(`${directBooking.date}T${directBooking.time}`).toLocaleDateString('sv-SE', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })} kl. {directBooking.time}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:py-4 border-t -mx-4 sm:-mx-8 -mb-4 sm:-mb-8 mt-4 rounded-b-none">
            <div className="flex items-center justify-between gap-2">
              <div>
                {isEditing && meeting.status === 'draft' && onDelete && (
                  confirmDelete ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Ta bort?</span>
                      <button
                        onClick={async () => {
                          try {
                            await onDelete(meeting._id);
                            onClose();
                          } catch (e) {
                            console.error('Kunde inte ta bort möte:', e);
                          }
                        }}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Ja, ta bort
                      </button>
                      <button
                        onClick={() => setConfirmDelete(false)}
                        className="text-gray-500 hover:text-gray-700 text-sm"
                      >
                        Avbryt
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(true)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Ta bort möte
                    </button>
                  )
                )}
              </div>

              <div className="flex flex-wrap gap-2 sm:gap-3">
                {/* Avbryt */}
                <button
                  onClick={onClose}
                  className="px-2.5 py-1.5 sm:px-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Avbryt
                </button>

                {/* Tillbaka (steg 2-3) */}
                {currentStep > 1 && (!isEditing || meeting.status === 'draft') && (
                  <button
                    onClick={handleBack}
                    className="px-2.5 py-1.5 sm:px-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Tillbaka
                  </button>
                )}

                {/* Spara som utkast (alla steg) */}
                {(!isEditing || meeting.status === 'draft') && (
                  <button
                    onClick={handleSaveAsDraft}
                    disabled={saving}
                    className="px-2.5 py-1.5 sm:px-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-1"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    {saving ? 'Sparar...' : 'Utkast'}
                  </button>
                )}

                {/* Nästa (steg 1-2) */}
                {currentStep < 3 && (!isEditing || meeting.status === 'draft') && (
                  <button
                    onClick={handleNext}
                    className="px-2.5 py-1.5 sm:px-3 text-sm font-medium bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink/90 transition-colors flex items-center gap-1"
                  >
                    Nästa
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}

                {/* Skicka tidsförslag (steg 3, propose) */}
                {currentStep === 3 && bookingMethod === 'propose' && (!isEditing || meeting.status === 'draft') && (
                  <button
                    onClick={handleSendProposals}
                    disabled={saving || !hasValidProposals()}
                    className="px-2.5 py-1.5 sm:px-3 text-sm font-medium bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink/90 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Skickar...' : 'Skicka tidsförslag'}
                  </button>
                )}

                {/* Boka mötet (steg 3, direct) */}
                {currentStep === 3 && bookingMethod === 'direct' && (!isEditing || meeting.status === 'draft') && (
                  <button
                    onClick={handleDirectBook}
                    disabled={saving || !hasValidDirectBooking()}
                    className="px-2.5 py-1.5 sm:px-3 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {saving
                      ? 'Bokar...'
                      : isRecurring && formData.type === 'byggmote'
                        ? `Boka ${getRecurringCount()} möten`
                        : 'Boka mötet'
                    }
                  </button>
                )}

                {/* Spara ändringar (befintligt möte som inte är utkast) */}
                {isEditing && meeting.status !== 'draft' && (
                  <button
                    onClick={handleSaveAsDraft}
                    disabled={saving}
                    className="px-2.5 py-1.5 sm:px-3 text-sm font-medium bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink/90 transition-colors disabled:opacity-50 flex items-center gap-1"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {saving ? 'Sparar...' : 'Spara ändringar'}
                  </button>
                )}
              </div>
            </div>
          </div>
    </Modal>
  );
};

export default MeetingModal;
